/**
 * An asset's lifecycle status.
 *
 * The name is always rendered — PRD §9.7 does not permit status by colour
 * alone, and four dots on one hue ramp are indistinguishable in greyscale.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.4, PRD §5.6, §9.7
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

import { ASSET_STATUS_DOT, type AssetStatus } from './asset-taxonomy';

export interface AssetStatusBadgeProps {
  status: AssetStatus;
  className?: string;
}

export function AssetStatusBadge({
  status,
  className,
}: AssetStatusBadgeProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <span
      className={cn(
        'bg-gray-100 text-navy-text inline-flex items-center gap-[6px] rounded-full',
        'font-display px-[11px] py-1 text-[10px] leading-[1.5] font-extrabold',
        'tracking-[0.1em] whitespace-nowrap uppercase',
        className,
      )}
    >
      {/* Decorative: the label beside it already names the status. */}
      <span
        aria-hidden="true"
        className={cn('size-[7px] rounded-full', ASSET_STATUS_DOT[status])}
      />
      {t(`asset.status.${status}`)}
    </span>
  );
}
